import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchWorksheets } from "../worksheetListSlice";
import { retrieveWorksheet } from "../worksheetSlice";
import { fetchLevels, fetchStudents } from "../../student/studentSlice";

import { formatDate } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";
import {
  ASSIGNMENT_PLAN_REQUIRED_MESSAGE,
  canCreateAssignment,
} from "../../../utils/planUtils";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import WorksheetModal from "./WorksheetModal/WorksheetModal";
import Pagination from "../../../components/Pagination";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";
import "./WorksheetTable.css";

const thStyle = {
  padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px",
  fontSize: "0.78rem",
  fontWeight: 700,
  color: "#7A8490",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", fontSize: "0.92rem" };

const iconBtnStyle = {
  width: "34px",
  height: "34px",
  backgroundColor: "#FFF",
  border: "1.5px solid #E6EDEC",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#3A434E",
};

const WorksheetTable = () => {
  const dispatch = useDispatch();

  const [isMobileW, setIsMobileW] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const { results, demo_results, current_page, total_pages } = useSelector(
    (state) => state.worksheets
  );
  const { allStudents } = useSelector((state) => state.student);
  const { user } = useSelector((state) => state.auth);

  const canAssign = canCreateAssignment(user.plan);

  const [keyword, setKeyword] = useState("");
  const [typeSelect, setTypeSelect] = useState("all");
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  // 지금 연 것이 체험용 문제지인지. 목록 줄에만 있는 표시라 모달로 들고 간다.
  const [demoWorksheet, setDemoWorksheet] = useState(null);

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page: 1, type: typeParam, keyword: keyword }));
    if (allStudents.length == 0) {
      dispatch(fetchStudents());
      dispatch(fetchLevels());
    }
  }, [dispatch, typeSelect, keyword]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handleWorksheetClick = async (item) => {
    await dispatch(
      retrieveWorksheet({
        id: item.id,
        type: item.type_name,
        author_name: item.user_name,
      })
    );
    setDemoWorksheet(item.is_demo ? item : null);
    setAssignmentModalOpen(true);
  };

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page, type: typeParam, keyword: keyword }));
  };

  const handlePreviewClick = (id, type) => {
    window.open(`https://www.ginedu.com/${type}/${id}/`, "_blank");
  };

  // 체험 줄과 본인 문제지 줄은 모양이 같으므로 한 곳에서 그린다.
  const renderWorksheetRow = (item, rowKey) => (
    <tr
      key={rowKey}
      style={{ borderBottom: "1px solid #E6EDEC" }}
    >
      <td
        style={{
          ...tdStyle,
          color: "#7A8490",
          fontSize: "0.84rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {item.id}
      </td>
      <td
        style={{
          ...tdStyle,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontWeight: 700,
          color: "#0E141B",
        }}
      >
        {item.title}
        <WorksheetTypeBox type={item.type_name} />
        {item.is_demo && (
          <span className="worksheet-table__demo-badge">체험</span>
        )}
      </td>
      <td
        style={{
          ...tdStyle,
          color: "#3A434E",
          fontSize: "0.86rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatDate(item.created_at)}
      </td>
      <td style={{ ...tdStyle, color: "#3A434E" }}>
        {item.user_name}
      </td>
      <td style={{ position: "relative" }}>
        <button
          onClick={() =>
            handlePreviewClick(item.id, item.type_name)
          }
          style={iconBtnStyle}
          title="문서 열기"
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#00D9C0";
            e.currentTarget.style.color = "#00A396";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#E6EDEC";
            e.currentTarget.style.color = "#3A434E";
          }}
        >
          <DescriptionIcon style={{ fontSize: "20px" }} />
        </button>
      </td>
      <td style={{ textAlign: "center" }}>
        <button
          style={{
            backgroundColor: "#0E141B",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "999px",
            padding: "8px 20px",
            cursor: "pointer",
            fontSize: "0.84rem",
            fontWeight: 700,
            fontFamily: "inherit",
            transition: "background-color .12s",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            // 플랜이 안 되는 사람에게도 버튼은 그대로 보여주고, 눌렀을 때
            // 안내한다. 예시 문제지만은 체험할 수 있게 열어 둔다.
            if (!canAssign && !item.is_demo) {
              alert(ASSIGNMENT_PLAN_REQUIRED_MESSAGE);
              return;
            }
            handleWorksheetClick(item);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#00A396";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#0E141B";
          }}
          title={
            item.is_demo
              ? "출제 화면을 체험해 봅니다 (실제로 출제되지 않습니다)"
              : canAssign
              ? "과제 출제하기"
              : "Plus, Premium 사용자만 출제 가능합니다"
          }
        >
          {item.is_demo ? "체험하기" : "출제하기"}
        </button>
      </td>
    </tr>
  );

  return (
    <div
      style={{
        padding: "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      {/* 페이지 헤더 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">
            문제지 전체 목록
          </h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div style={{ flex: isMobileW ? 1 : "none", width: isMobileW ? "auto" : "30%", minWidth: isMobileW ? 0 : "220px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>

      {/* 표 카드 */}
      <div
        style={{
          backgroundColor: "#FFF",
          border: "1px solid #E6EDEC",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
          paddingBottom: "5px",
          overflowX: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table style={{ width: "100%", minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "750px" : undefined, borderCollapse: "collapse" }}>
          <colgroup>
            <col style={{ width: "10%" }} />
            <col />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1.5px solid #0E141B",
              }}
            >
              <td style={thStyle}>문항 번호</td>
              <td style={thStyle}>학습지명</td>
              <td style={thStyle}>생성일</td>
              <td style={thStyle}>출제자</td>
              <td style={{ ...thStyle, textAlign: "center" }}>미리보기</td>
              <td style={{ ...thStyle, textAlign: "center" }}>출제</td>
            </tr>
            {demo_results &&
              demo_results.map((item) =>
                renderWorksheetRow(item, `demo-${item.id}`)
              )}
            {results &&
              results.map((item) => renderWorksheetRow(item, item.id))}
          </tbody>
        </table>

        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />

        {assignmentModalOpen && (
          <WorksheetModal
            isDemo={Boolean(demoWorksheet)}
            onClose={() => setAssignmentModalOpen(false)}
          ></WorksheetModal>
        )}
      </div>
    </div>
  );
};

export default WorksheetTable;
